import { useUpdateProject } from '@consigli/hooks';
import { Button } from '@consigli/shadcn';
import { Project, Services } from '@consigli/types';
import { FC, useCallback, useState } from 'react';
import { HiOutlinePencil } from 'react-icons/hi';

import { SheetForm } from '@/components';
import { errorToast, successToast } from '@/components/toast';

import { AvailableServicesFields } from './available-services-fields';
import { EditProjectFormData, FormErrors } from './types';
import { validateEditProjectForm } from './validation';

interface EditProjectFormProps {
  project: Project;
  trigger?: React.ReactNode;
  onSuccess?: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Edit Project Form for updating project settings.
 * Currently supports editing available services override.
 * Supports both controlled (open/onOpenChange) and uncontrolled modes.
 */
export const EditProjectForm: FC<EditProjectFormProps> = ({
  project,
  trigger,
  onSuccess,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}) => {
  const { updateProject, isLoading } = useUpdateProject();

  const [internalOpen, setInternalOpen] = useState(false);

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;

  const setIsOpen = useCallback(
    (open: boolean) => {
      if (isControlled) {
        controlledOnOpenChange?.(open);
      } else {
        setInternalOpen(open);
      }
    },
    [isControlled, controlledOnOpenChange],
  );

  const [errors, setErrors] = useState<FormErrors>({});
  const [formData, setFormData] = useState<EditProjectFormData>({
    availableServicesOverride: (project.availableServicesOverride as Services[]) ?? [],
  });

  const resetForm = useCallback(() => {
    setFormData({
      availableServicesOverride: (project.availableServicesOverride as Services[]) ?? [],
    });
    setErrors({});
  }, [project]);

  const handleFieldChange = useCallback(
    (field: keyof EditProjectFormData, value: unknown) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    },
    [errors],
  );

  const handleSubmit = useCallback(async (): Promise<boolean> => {
    const validation = validateEditProjectForm(formData);

    if (!validation.isValid) {
      setErrors(validation.errors);
      return false;
    }

    try {
      await updateProject({
        projectId: project.id,
        data: {
          availableServicesOverride: formData.availableServicesOverride,
        },
      }).unwrap();

      successToast({ message: 'Project updated successfully!' });
      setIsOpen(false);
      onSuccess?.();
      return true;
    } catch (error) {
      console.error('Failed to update project:', error);
      errorToast({ message: 'Failed to update project. Please try again.' });
      return false;
    }
  }, [formData, project.id, updateProject, onSuccess, setIsOpen]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (open) {
        resetForm();
      }
    },
    [resetForm, setIsOpen],
  );

  return (
    <SheetForm
      title={`Edit ${project.name}`}
      description="Update project settings and available services."
      trigger={
        trigger || (
          <Button variant="outline">
            <HiOutlinePencil />
            Edit Project
          </Button>
        )
      }
      submitLabel="Save Changes"
      cancelLabel="Cancel"
      onSubmit={handleSubmit}
      onCancel={resetForm}
      isSubmitting={isLoading}
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <div className="space-y-6">
        <AvailableServicesFields
          formData={formData}
          errors={errors}
          onFieldChange={handleFieldChange}
          orgServices={(project.availableServices as Services[]) ?? []}
          usedExternalServices={(project.usedExternalServices as Services[]) ?? []}
        />
      </div>
    </SheetForm>
  );
};
